import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { ThemeContextProvider } from '../contexts/ThemeContext';

import LoadingSpinner from '../components/LoadingSpinner';
import ProtectedRoute from '../router/ProtectedRoute';
import HomeRedirect from '../router/HomeRedirect';

// Layouts
import TenantLayout from '../layouts/TenantLayout';
import CompanyLayout from '../layouts/CompanyLayout';

// ── Layer 2 — Tenant Admin ────────────────────────────────────────────────────
import TenantDashboardPage from './tenant/TenantDashboardPage';
import TenantCompaniesPage from './tenant/TenantCompaniesPage';
import TenantBillingPage from './tenant/TenantBillingPage';
import TenantInvoicesPage from './tenant/TenantInvoicesPage';
import TenantUsersPage from './tenant/TenantUsersPage';
import TenantSettingsPage from './tenant/Tenantsettingspage';

// ── Layer 3 — Company Users ───────────────────────────────────────────────────
import Dashboard from './company/Dashboard';
import AnalyticsPage from './company/AnalyticsPage';
import MapPage from './company/MapPage';
import DevicesPage from './company/DevicesPage';
import DriversPage from './company/Driverspage';
import FleetManagementPage from './company/FleetManagementPage';
import VehicleRequestsPage from './company/VehicleRequestsPage';
import TripPlaybackPage from './company/TripPlaybackPage';
import GeofencingDashboard from './company/GeofencingDashboard';
import SpeedMonitoringPage from './company/SpeedMonitoringPage';
import TelemetryPage from './company/TelemetryPage';
import DailyMileagePage from './company/DailyMileagePage';
import ActivityPage from './company/ActivityPage';
import WorkOrdersPage from './company/WorkOrdersPage';
import UserManagementPage from './company/UserManagementPage';
import CompanySettingsPage from './company/CompanySettingsPage';
import InvoicesPage from './company/InvoicesPage';

// ── Public ────────────────────────────────────────────────────────────────────
import LoginPage from './LoginPage';
import CompanyRegisterPage from './CompanyRegisterPage';
import AcceptInvitationPage from './AcceptInvitationPage';


import StaffRequestPortal from './public/StaffRequestPortal';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

const Stub: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ padding: 32 }}>{label} — Coming Soon</div>
);

// ── Route wrappers ────────────────────────────────────────────────────────────
const TENANT_ROLES = ['tenant_admin', 'tenant_support', 'tenant_billing'];
const TenantRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute roles={TENANT_ROLES} fallbackPath="/dashboard">
    <TenantLayout>{children}</TenantLayout>
  </ProtectedRoute>
);

const CompanyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <CompanyLayout>{children}</CompanyLayout>
  </ProtectedRoute>
);

const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return (
    <Routes>
      {/* ── Public ───────────────────────────────────────────────────────── */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register-company" element={<CompanyRegisterPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

      <Route path="/request-portal/:portalToken" element={<StaffRequestPortal />} />


      {/* ── Root ─────────────────────────────────────────────────────────── */}
      <Route
        path="/"
        element={isAuthenticated ? <HomeRedirect /> : <Navigate to="/login" replace />}
      />

      {/* ════════════════════════════════════════════════════════════════════
          LAYER 2 — TENANT ADMIN
          ════════════════════════════════════════════════════════════════════ */}
      <Route path="/tenant" element={<TenantRoute><TenantDashboardPage /></TenantRoute>} />
      <Route path="/tenant/companies" element={<TenantRoute><TenantCompaniesPage /></TenantRoute>} />
      <Route path="/tenant/billing" element={<TenantRoute><TenantBillingPage /></TenantRoute>} />
      <Route path="/tenant/invoices" element={<TenantRoute><TenantInvoicesPage /></TenantRoute>} />
      <Route path="/tenant/users" element={<TenantRoute><TenantUsersPage /></TenantRoute>} />
      <Route path="/tenant/settings" element={<TenantRoute><TenantSettingsPage /></TenantRoute>} />

      {/* ════════════════════════════════════════════════════════════════════
          LAYER 3 — COMPANY USERS
          ════════════════════════════════════════════════════════════════════ */}
      <Route path="/dashboard" element={<CompanyRoute><Dashboard /></CompanyRoute>} />
      <Route path="/analytics" element={<CompanyRoute><AnalyticsPage /></CompanyRoute>} />
      <Route path="/map" element={<CompanyRoute><MapPage /></CompanyRoute>} />
      <Route path="/devices" element={<CompanyRoute><DevicesPage /></CompanyRoute>} />
      <Route path="/drivers" element={<CompanyRoute><DriversPage /></CompanyRoute>} />
      <Route path="/fleet-management" element={<CompanyRoute><FleetManagementPage /></CompanyRoute>} />
      <Route path="/vehicle-requests" element={<CompanyRoute><VehicleRequestsPage /></CompanyRoute>} />
      <Route path="/trip-playback" element={<CompanyRoute><TripPlaybackPage /></CompanyRoute>} />
      <Route path="/geofencing" element={<CompanyRoute><GeofencingDashboard /></CompanyRoute>} />
      <Route path="/speed-monitoring" element={<CompanyRoute><SpeedMonitoringPage /></CompanyRoute>} />
      <Route path="/telemetry" element={<CompanyRoute><TelemetryPage /></CompanyRoute>} />
      <Route path="/daily-mileage" element={<CompanyRoute><DailyMileagePage /></CompanyRoute>} />
      <Route path="/activity" element={<CompanyRoute><ActivityPage /></CompanyRoute>} />
      <Route path="/work-orders" element={<CompanyRoute><WorkOrdersPage /></CompanyRoute>} />
      <Route path="/admin/users" element={<CompanyRoute><UserManagementPage /></CompanyRoute>} />
      <Route path="/admin/company" element={<CompanyRoute><CompanySettingsPage /></CompanyRoute>} />
      <Route path="/invoices" element={<CompanyRoute><InvoicesPage /></CompanyRoute>} />

      {/* Stubs */}
      <Route path="/maintenance" element={<CompanyRoute><Stub label="Maintenance" /></CompanyRoute>} />
      <Route path="/messages" element={<CompanyRoute><Stub label="Messages" /></CompanyRoute>} />
      <Route path="/fill-ups" element={<CompanyRoute><Stub label="Fill-ups" /></CompanyRoute>} />
      <Route path="/trips" element={<CompanyRoute><Stub label="Trips" /></CompanyRoute>} />
      <Route path="/timesheet" element={<CompanyRoute><Stub label="Daily Timesheet" /></CompanyRoute>} />
      <Route path="/services" element={<CompanyRoute><Stub label="Services" /></CompanyRoute>} />
      <Route path="/expenses" element={<CompanyRoute><Stub label="Other Expenses" /></CompanyRoute>} />
      <Route path="/reminders" element={<CompanyRoute><Stub label="Reminders" /></CompanyRoute>} />
      <Route path="/inspections" element={<CompanyRoute><Stub label="Vehicle Inspections" /></CompanyRoute>} />
      <Route path="/issues" element={<CompanyRoute><Stub label="Issues" /></CompanyRoute>} />
      <Route path="/groups" element={<CompanyRoute><Stub label="Groups" /></CompanyRoute>} />
      <Route path="/admin" element={<CompanyRoute><Stub label="Administration" /></CompanyRoute>} />

      {/* Fallback */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <CssBaseline />
    <AuthProvider>
      <ThemeContextProvider>
        <NotificationProvider>
          <Router>
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
              <AppContent />
            </Box>
          </Router>
        </NotificationProvider>
      </ThemeContextProvider>
    </AuthProvider>
    <ReactQueryDevtools initialIsOpen={false} position="bottom" />
  </QueryClientProvider>
);

export default App;